import React, { useState } from 'react';
import { useBattle } from '../context/BattleProvider';
import { useLiveKitRoom } from '../hooks/useLiveKitRoom';
import { ParticipantTile } from '../components/ParticipantTile';
import { BackingTrackPanel } from '../components/BackingTrackPanel';
import { BattleChat } from '../components/BattleChat';
import toast from 'react-hot-toast';

// Full host interface: manage the session, approve participants, control the stage.
export const HostView: React.FC = () => {
  const {
    session,
    livekitToken,
    livekitUrl,
    joinRequests,
    approveRequest,
    denyRequest,
    hostGoLive,
    hostEndSession,
    hostPinParticipant,
    inviteUrl,
  } = useBattle();

  const { isConnected, remoteParticipants } = useLiveKitRoom({
    url: livekitUrl,
    token: livekitToken,
  });

  const [copied, setCopied] = useState(false);

  const handleCopyInvite = () => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGoLive = async () => {
    await hostGoLive();
    toast.success('Session is now live!');
  };

  const handleEnd = async () => {
    if (!window.confirm('End the battle session for everyone?')) return;
    await hostEndSession();
  };

  const pendingRequests = joinRequests.filter((r) => r.status === 'pending');

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 300px',
        gap: 16,
        height: 'calc(100vh - 60px)',
        padding: 20,
        boxSizing: 'border-box',
      }}
    >
      {/* ── LEFT: stage ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>
        {/* Session header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 800 }}>
            {session?.title}
          </h2>
          <span
            className="pill"
            style={{
              background:
                session?.status === 'live' ? 'rgba(74,222,128,0.15)' : 'var(--accent-dim)',
              color: session?.status === 'live' ? '#4ade80' : 'var(--accent)',
            }}
          >
            {session?.status === 'live' ? 'LIVE' : 'Waiting'}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
            {isConnected ? 'Connected' : 'Connecting…'}
          </span>
        </div>

        {/* Participant grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 10,
            flex: 1,
            overflowY: 'auto',
          }}
        >
          {remoteParticipants.length === 0 ? (
            <div
              style={{ color: 'var(--text-muted)', fontSize: 14, padding: 24, gridColumn: '1/-1' }}
            >
              No participants yet. Share the invite link to get started.
            </div>
          ) : (
            remoteParticipants.map((p) => (
              <ParticipantTile
                key={p.identity}
                participant={p}
                isPinned={session?.pinned_participant === p.identity}
                isHost={true}
                onPin={() =>
                  hostPinParticipant(session?.pinned_participant === p.identity ? null : p.identity)
                }
              />
            ))
          )}
        </div>

        {/* Controls row */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {session?.status === 'waiting' && (
            <button className="btn-primary" onClick={handleGoLive}>
              Go live
            </button>
          )}
          <button className="btn-ghost" onClick={handleCopyInvite}>
            {copied ? 'Copied!' : 'Copy invite link'}
          </button>
          <button
            className="btn-ghost"
            style={{ marginLeft: 'auto', color: '#f87171', borderColor: 'rgba(248,113,113,0.3)' }}
            onClick={handleEnd}
          >
            End session
          </button>
        </div>
      </div>

      {/* ── RIGHT: sidebar ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
        {/* Join request queue */}
        {pendingRequests.length > 0 && (
          <div
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              padding: '12px 14px',
            }}
          >
            <p className="section-label">Join requests ({pendingRequests.length})</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {pendingRequests.map((req) => (
                <div key={req.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {req.avatar_url ? (
                    <img
                      src={req.avatar_url}
                      alt={req.username}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '1px solid var(--border)',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: 'var(--accent-dim)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        color: 'var(--accent)',
                        fontWeight: 600,
                      }}
                    >
                      {req.username[0]?.toUpperCase()}
                    </div>
                  )}
                  <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1 }}>
                    {req.username}
                  </span>
                  <button
                    style={{
                      fontSize: 12,
                      padding: '4px 10px',
                      background: 'var(--accent)',
                      color: '#1a0050',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                    onClick={() => {
                      approveRequest(req.id);
                      toast.success(req.username + ' approved');
                    }}
                  >
                    ✓
                  </button>
                  <button
                    style={{
                      fontSize: 12,
                      padding: '4px 10px',
                      background: 'transparent',
                      color: '#f87171',
                      border: '1px solid rgba(248,113,113,0.3)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      denyRequest(req.id);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Backing track */}
        <BackingTrackPanel />

        {/* Chat */}
        <div
          style={{
            flex: 1,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <BattleChat />
        </div>
      </div>
    </div>
  );
};
